import { computed, ref, watch, type Ref } from 'vue';
import {
  getDeeplinks,
  type Deeplinks,
  type Deeplink,
  type GetDeeplinksParams,
  type ProductType,
} from './../services/VehicleDeeplinks/index';
import { defineStore } from './store';

export const useVehicleDeepLinksStore = defineStore('pi-vehicleDeepLinks', () => {
  const map = new Map<string, Ref<VehicleDeepLinkState | undefined>>();

  function get(params: DeeplinkCache) {
    // Ensure we have the most recent map key
    const keyr = computed(() =>
      params.brandId.value
        ? `${params.country}-${params.language}-${params.modelSeries}-${params.brandId.value}`
        : undefined,
    );
    watch(
      keyr,
      (key) => {
        if (!key || map.has(key)) return;

        const _ref = ref<VehicleDeepLinkState | undefined>();

        map.set(key, _ref);

        const _params = {
          ...params,
          brandId: params.brandId.value,
        };

        getDeeplinks(_params).then(
          (dl) => {
            _ref.value = mapDeepLinks(dl);
          },
          () => {
            _ref.value = {} as VehicleDeepLinkState;
          },
        );
      },
      {
        immediate: true,
      },
    );

    return computed(() => (keyr.value ? map.get(keyr.value)?.value : undefined));
  }
  return { get };
});

export type VehicleDeepLinkState = Record<ProductType, VehicleDeepLink>;

interface VehicleDeepLink extends Deeplink {
  type: ProductType;
  icon: string;
}

export type DeeplinkCache = Omit<GetDeeplinksParams, 'brandId'> & {
  brandId: Ref<string | undefined>;
};

export const mapDeepLinks = (deeplinks: Deeplinks) =>
  Object.fromEntries(
    Object.entries(deeplinks).map<[ProductType, VehicleDeepLink]>(([key, value]) => [
      key as ProductType,
      {
        type: key as ProductType,
        url: value.url,
        label: value.label,
        icon: iconMap[key as ProductType],
      },
    ]),
  ) as VehicleDeepLinkState;

const iconMap: Record<ProductType, string> = {
  CONFIGURATOR: 'bds/car-config/24',
  ONLINE_SHOP: 'bds/shopping-cart/24',
  TEST_DRIVE: 'bds/test-drive/24',
  PRODUCT_PAGE: '', //not used
};
